import { parseMonarchError } from "~/helpers/monarchErrorReporting";

/** localStorage key set by the Cohesion client when a Monarch session enters errored state. */
export const MONARCH_ERROR_STORAGE_KEY = "monarchError";

/** Max wait for requestIdleCallback before forcing the recovery retry. */
export const MONARCH_RECOVERY_IDLE_TIMEOUT_MS = 2000;

/**
 * Returns true for transient Monarch client failures we can recover from by clearing
 * `monarchError` and re-requesting (timeout / session-errored cascade).
 */
export const isRecoverableMonarchError = (err: unknown): boolean => {
  if (err == null) return false;
  const text = parseMonarchError(err).message.toLowerCase();
  return text.includes("timed out") || text.includes("session in errored state") || text.includes("no monarch evaluation requested");
};
export const clearMonarchSessionError = (): boolean => {
  try {
    if (typeof localStorage === "undefined") return false;
    if (localStorage.getItem(MONARCH_ERROR_STORAGE_KEY) == null) return false;
    localStorage.removeItem(MONARCH_ERROR_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};
export const hasMonarchSessionError = (): boolean => {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(MONARCH_ERROR_STORAGE_KEY) != null;
  } catch {
    return false;
  }
};

/** Run `cb` when the browser is idle, falling back to a fixed delay. */
export const runWhenIdle = (cb: () => void, timeoutMs = MONARCH_RECOVERY_IDLE_TIMEOUT_MS): (() => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(() => cb(), {
      timeout: timeoutMs
    });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, timeoutMs);
  return () => window.clearTimeout(id);
};